import LogService from "../../CommonSdk/LogService";

import * as AuthenticationService from "../../RainbowSdk/AuthenticationService";
import * as AuthenticationActionTypes from "./AuthenticationActionTypes";
import * as ContactsActions from "./ContactsActions";
import * as CommonActions from "./CommonActions";
import * as AuthenticationUtils from "../../Utils/AuthenticationUtils";
import * as ContactUtils from "../../Utils/ContactUtils";
import * as ErrorUtils from "../../Utils/ErrorUtils";
import jwt from "jwt-decode";
import RainbowServerFetch from "../../Request/RainbowServerFetch";
import * as StorageKeys from "../../Constants/StorageKeys";

const LOG_TAG = "ACTIONS:AUTHENTICATION";
export const DURATION_24H_IN_SECONDS = 3600 * 24;

const baseServerSuccess = (baseServerUrl) => {
    return {
        type: AuthenticationActionTypes.AUTHENTICATION_ENVIRONMENT_SET_BASE_SERVER,
        payload: {
            baseServerUrl: baseServerUrl
        }
    };
};

const loginSuccess = (token) => {
    localStorage.setItem(StorageKeys.STORAGE_KEY_TOKEN, token);
    return {
        type: AuthenticationActionTypes.AUTHENTICATION_LOGGED_IN,
        payload: {
            token: token
        }
    };
};

const logoutSuccess = () => {
    localStorage.removeItem(StorageKeys.STORAGE_KEY_TOKEN);
    return {
        type: AuthenticationActionTypes.AUTHENTICATION_LOGGED_OUT
    };
};

const onLoginActions = (serverUrl, token, user) => (dispatch) => {
    RainbowServerFetch.initialise(serverUrl, token);
    dispatch(loginSuccess(token));
    dispatch(ContactsActions.setUserContact(user));

    let needGetUserPromise = Promise.resolve(user);
    if (!ContactUtils.isUserFullyLoaded(user))
        needGetUserPromise = dispatch(ContactsActions.getUserContact());

    needGetUserPromise
        .then(userContact => {
            LogService.info(LOG_TAG, "onLoginActions", "Check role [" + userContact.roles + "] for user:", userContact.id);
            dispatch(CommonActions.onLoginActions(userContact))
        })
        .catch(errorPayload => {
            LogService.error(LOG_TAG, "onLoginActions", "error", errorPayload);
        });
}

const onLogoutActions = () => (dispatch) => {
    dispatch(logoutSuccess());
    dispatch(CommonActions.onLogoutActions());
    RainbowServerFetch.reset();
}

export const login = (data, logoutOnFailure = false) => (dispatch, getState) => {
    LogService.trace(LOG_TAG, "login", "try to log-in user", data.email);

    if (!AuthenticationUtils.isRainbowAuthentication(getState().authentication.account)) {
        LogService.error(LOG_TAG, "Login. Not rainbow authentication. not supported");
        const errorPayload = ErrorUtils.parseErrorAndReturnPayload("Login. Not rainbow authentication. Not supported");
        return Promise.reject(errorPayload);
    }

    const loginUrl = AuthenticationUtils.getSelectedLoginUrl(getState().authentication.account);
    const selectedServerUrl = AuthenticationUtils.getSelectedServerUrl(getState().authentication.account);

    return AuthenticationService.login(loginUrl, data.email, data.password)
        .then(json => {
            LogService.info(LOG_TAG, "login", "successfully logged-in on", loginUrl);

            const user = json.loggedInUser;
            dispatch(onLoginActions(selectedServerUrl, json.token, user));
            return Promise.resolve();
        })
        .catch(errorPayload => {
            LogService.error(LOG_TAG, "login", "error", errorPayload);
            if (logoutOnFailure) {
                dispatch(logout());
            }
            return Promise.reject(errorPayload)
        });
};

export const logout = () => (dispatch, getState) => {
    LogService.trace(LOG_TAG, "logout", "log-out current user");

    // let logoutUrl = AuthenticationUtils.getSelectedLogoutUrl(getState().authentication.account);
    const logoutUrl = RainbowServerFetch.getServerUrl() + "/api/rainbow/authentication/v1.0/logout";
    //CT: always use rainbow logout (even if sso, otherwise logout will also be applied to other applications using sso)
    const token = getState().authentication.account.token;

    return AuthenticationService.logout(logoutUrl, token)
        .then(json => {
            LogService.info(LOG_TAG, "logout", "success");
            dispatch(onLogoutActions());
            return Promise.resolve();
        })
        .catch(errorPayload => {
            LogService.error(LOG_TAG, "logout", "error", errorPayload);
            dispatch(onLogoutActions());
            return Promise.reject(errorPayload);
        });
};

export const getUserEnvironment = (email) => (dispatch, getState) => {
    LogService.trace(LOG_TAG, "getUserEnvironment", "enter");

    let baseServerUrl = AuthenticationUtils.getBaseServerUrl(getState().authentication.account);
    return AuthenticationService.getUserEnvironment(baseServerUrl, email)
        .then(json => {
            LogService.info(LOG_TAG, "getUserEnvironment", "successfully received");

            dispatch({
                type: AuthenticationActionTypes.AUTHENTICATION_ENVIRONMENT_SUCCESS,
                payload: {
                    environment: json.data
                }
            });
            return Promise.resolve(json.data);
        })
        .catch(errorPayload => {
            LogService.error(LOG_TAG, "getUserEnvironment", "error", errorPayload);
            return Promise.reject(errorPayload);
        });
}

export const getUserAuthentication = (email) => (dispatch, getState) => {
    LogService.trace(LOG_TAG, "getUserAuthentication", "enter");

    const selectedServerUrl = AuthenticationUtils.getSelectedServerUrl(getState().authentication.account);
    return AuthenticationService.getUserAuthentication(selectedServerUrl, email)
        .then(json => {
            LogService.info(LOG_TAG, "getUserAuthentication", "successfully received");

            let authentication = json.data;
            dispatch({
                type: AuthenticationActionTypes.AUTHENTICATION_SUCCESS,
                payload: {
                    authentication: authentication
                }
            });
            return Promise.resolve(authentication);
        })
        .catch(errorPayload => {
            LogService.error(LOG_TAG, "getUserAuthentication", "error", errorPayload);
            return Promise.reject(errorPayload);
        });
}

export const authenticationCheckState = () => (dispatch, getState) => {
    LogService.trace(LOG_TAG, "authenticationCheckState", "enter");
    const token = localStorage.getItem(StorageKeys.STORAGE_KEY_TOKEN);
    if (!token) {
        LogService.info(LOG_TAG, "authenticationCheckState", "no token found");
        dispatch(onLogoutActions());
        return Promise.resolve();
    }

    let decodedToken = null;
    try {
        decodedToken = jwt(token);
        LogService.info(LOG_TAG, "authenticationCheckState", "token found");
    } catch (error) {
        LogService.error(LOG_TAG, "authenticationCheckState", "error with token", error);
        dispatch(onLogoutActions());
        return Promise.resolve();
    }

    const expirationDate = new Date(decodedToken.exp * 1000);
    const expiration24hDate = new Date(decodedToken.iat * 1000 + DURATION_24H_IN_SECONDS * 1000);
    const loginDate = new Date(decodedToken.iat * 1000);
    LogService.info(LOG_TAG, "authenticationCheckState", "login date", loginDate);
    LogService.info(LOG_TAG, "authenticationCheckState", "login date1", expiration24hDate);
    const dateNow = new Date();
    if (expirationDate <= dateNow) {
        LogService.warn(LOG_TAG, "authenticationCheckState", "credential expired -> logout");
        dispatch(onLogoutActions());
        return Promise.resolve();
    }
    if (expiration24hDate <= dateNow) {
        LogService.warn(LOG_TAG, "authenticationCheckState", "more than 24h connected -> logout");
        dispatch(onLogoutActions());
        return Promise.resolve();
    }

    const userId = decodedToken.user.id;
    const email = decodedToken.user.loginEmail;
    const currentServerUrl = decodedToken.environment.environmentApiUrl;
    LogService.info(LOG_TAG, "authenticationCheckState", "userId", userId, "email", email);
    LogService.info(LOG_TAG, "authenticationCheckState", "currentServerUrl", currentServerUrl);
    return dispatch(getUserEnvironment(email))
        .then((environment) => {
            LogService.info(LOG_TAG, "authenticationCheckState", "get environment success");
            const index = environment.findIndex((env) => env.environmentApiUrl === currentServerUrl);
            if (index < 0) {
                LogService.warn(LOG_TAG, "authenticationCheckState", "environment, not same server found");
                return Promise.reject(ErrorUtils.parseErrorAndReturnPayload("environment, not same server found"));
            }
            dispatch(selectUserEnvironment(index));
            return dispatch(getUserAuthentication(email));
        })
        .then((authentication) => {
            LogService.info(LOG_TAG, "authenticationCheckState", "get authentication success");
            let authenticationType = AuthenticationUtils.AUTHENTICATION_TYPES.RAINBOW;
            if (decodedToken.oidc)
                authenticationType = AuthenticationUtils.AUTHENTICATION_TYPES.OIDC;
            else if (decodedToken.saml)
                authenticationType = AuthenticationUtils.AUTHENTICATION_TYPES.SAML;

            const index = authentication.findIndex((auth) => auth.type === authenticationType);
            if (index < 0) {
                LogService.warn(LOG_TAG, "authenticationCheckState", "authentication, not same authentication found");
                return Promise.reject(ErrorUtils.parseErrorAndReturnPayload("authentication, not same authentication found"));
            }
            LogService.debug(LOG_TAG, "authenticationCheckState", "authentication type", authenticationType, " index", index);
            dispatch(selectUserAuthentication(index));
            return Promise.resolve();
        })
        .then(() => {
            dispatch(onLoginActions(currentServerUrl, token, { id: userId }));
            return Promise.resolve();
        })
        .catch((errorPayload) => {
            LogService.error(LOG_TAG, "authenticationCheckState", "error", errorPayload);
            dispatch(onLogoutActions());
            return Promise.resolve();
        });
}

export const clearEnvironment = () => dispatch => {
    LogService.trace(LOG_TAG, "clearEnvironment");
    dispatch({
        type: AuthenticationActionTypes.AUTHENTICATION_ENVIRONMENT_CLEAR
    });
};

export const clearAuthentication = () => dispatch => {
    LogService.trace(LOG_TAG, "clearAuthentication");
    dispatch({
        type: AuthenticationActionTypes.AUTHENTICATION_CLEAR
    });
};

export const setBaseServerEnvironment = (baseServerUrl) => dispatch => {
    LogService.info(LOG_TAG, "setBaseServerEnvironment", baseServerUrl);
    dispatch(baseServerSuccess(baseServerUrl));
};

export const selectUserEnvironment = (index) => dispatch => {
    LogService.trace(LOG_TAG, "selectUserEnvironment");
    dispatch({
        type: AuthenticationActionTypes.AUTHENTICATION_ENVIRONMENT_SELECT,
        payload: {
            selectedServerIndex: index
        }
    });
};

export const selectUserAuthentication = (index) => dispatch => {
    LogService.trace(LOG_TAG, "selectUserAuthentication");
    dispatch({
        type: AuthenticationActionTypes.AUTHENTICATION_SELECT,
        payload: {
            selectedLoginIndex: index
        }
    });
};

export const getSsoLoginUrl = (loginUrl, challenge, version) => dispatch => {
    return AuthenticationService.getSsoLoginUrl(loginUrl, challenge, version);
}

export const loginSso = (token) => (dispatch) => {
    LogService.info(LOG_TAG, "loginSso", "successfully logged-in");

    localStorage.setItem(StorageKeys.STORAGE_KEY_TOKEN, token);
    return dispatch(authenticationCheckState());
};
